import { Box, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";

import {
  attendanceCellSx,
  attendanceOffDaySx,
  compactStatusSymbol,
  formatAttendanceCellLabel,
  formatAttendanceHoursLabel,
  isOffWeekday,
} from "@/containers/staff/staff-attendance/attendance-cell-display";
import { weekdayShortFromIndex } from "@/containers/staff/staff-attendance/attendance-grid-layout";
import type {
  AttendanceDayCell,
  AttendanceEmployeeRow,
} from "@/containers/staff/staff-attendance/attendance-types";
import { formatListDate, formatWeekdayFull } from "@/utils/date-format";

import {
  calendarLeadingPadCount,
  calendarTrailingPadCount,
  daysInMonthFromKey,
} from "../staff-show-helpers";

/** Fixed cell size so every day (with or without punches) aligns in the grid. */
const CELL_MIN_HEIGHT = 76;

type Props = {
  month: string;
  timezone: string;
  offWeekdays: number[];
  employee: AttendanceEmployeeRow | null;
  onDayClick: (day: number, cell: AttendanceDayCell) => void;
};

function PadCell() {
  return (
    <Box
      aria-hidden
      sx={{
        minHeight: CELL_MIN_HEIGHT,
        border: 1,
        borderColor: "transparent",
        borderRadius: 1,
      }}
    />
  );
}

export function StaffAttendanceMonthCalendar({
  month,
  timezone,
  offWeekdays,
  employee,
  onDayClick,
}: Props) {
  const days = daysInMonthFromKey(month);
  const leading = calendarLeadingPadCount(month);
  const trailing = calendarTrailingPadCount(month);

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Attendance calendar
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.75,
          width: "100%",
          maxWidth: 720,
        }}
      >
        {Array.from({ length: 7 }, (_, weekday) => (
          <Box
            key={`hdr-${weekday}`}
            sx={(theme) => ({
              textAlign: "center",
              typography: "caption",
              fontWeight: 600,
              color: "text.secondary",
              py: 0.5,
              ...(isOffWeekday(weekday, offWeekdays) ? attendanceOffDaySx(theme) : {}),
              borderRadius: 1,
            })}
          >
            {weekdayShortFromIndex(weekday)}
          </Box>
        ))}

        {Array.from({ length: leading }, (_, i) => (
          <PadCell key={`lead-${i}`} />
        ))}

        {days.map((day) => {
          const date = dayjs(`${month}-${String(day).padStart(2, "0")}`);
          const weekday = date.day();
          const isOff = isOffWeekday(weekday, offWeekdays);
          const cell = employee?.days[day];
          const resolved: AttendanceDayCell = cell ?? {
            status: isOff ? "OFF" : "ABSENT",
            checkIn: null,
            checkOut: null,
            hours: null,
            punches: [],
          };
          const isQuietCell =
            resolved.status === "ABSENT" ||
            (resolved.status === "OFF" && resolved.punches.length === 0);
          const symbol = compactStatusSymbol(resolved.status);
          const timesLabel = formatAttendanceCellLabel(resolved, "times", timezone);
          const hoursLabel = formatAttendanceHoursLabel(resolved.hours);
          const tip = isQuietCell
            ? [
                formatWeekdayFull(date),
                formatListDate(date),
                resolved.status === "OFF" ? "Off day" : "Absent",
              ].join(" · ")
            : [
                formatWeekdayFull(date),
                formatListDate(date),
                `${symbol} — ${timesLabel}`,
                hoursLabel,
              ].join(" · ");

          return (
            <Tooltip key={day} title={tip}>
              <Box
                component="button"
                type="button"
                onClick={() => onDayClick(day, resolved)}
                sx={(theme) => ({
                  width: "100%",
                  minHeight: CELL_MIN_HEIGHT,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.25,
                  px: 0.5,
                  py: 0.5,
                  typography: "caption",
                  lineHeight: 1.15,
                  overflow: "hidden",
                  ...attendanceCellSx(theme, resolved, { isOffColumn: isOff }),
                })}
              >
                <Box component="span" sx={{ opacity: 0.8, fontWeight: 600 }}>
                  {day}
                </Box>
                {isQuietCell ? null : (
                  <>
                    <Box component="span" fontWeight={600}>
                      {symbol}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.65rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        fontWeight: resolved.late ? 700 : undefined,
                        minHeight: "0.85rem",
                      }}
                    >
                      {timesLabel}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.65rem",
                        minHeight: "0.85rem",
                        opacity: resolved.hours != null ? 1 : 0.7,
                      }}
                    >
                      {hoursLabel}
                    </Box>
                  </>
                )}
              </Box>
            </Tooltip>
          );
        })}

        {Array.from({ length: trailing }, (_, i) => (
          <PadCell key={`trail-${i}`} />
        ))}
      </Box>
    </Box>
  );
}
